import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";

import { faCalendarDays, faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faUser } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	AvatarProfile,
	ButtonStandard,
	Calendar,
	Chip,
	InputText,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Tooltip,
} from "@performyard/py-components";

import type { ChecklistItemSaveData, ItemType, WorkspaceParticipant } from "../types";

import { config } from "../config";

type ChecklistItemFormProps = {
	id: string;
	itemType: ItemType;
	initialName: string;
	initialDueDate?: Date | null;
	initialAssigneeId?: string | null;
	isNewItem?: boolean;
	/** Required when itemType is "action"; ignored for agenda items. */
	workspaceParticipants?: WorkspaceParticipant[];
	onSave: (save: boolean, data?: ChecklistItemSaveData) => void;
};

const ITEM_TYPE_COPY = {
	agenda: { placeholder: "Your agenda item goes here", addLabel: "Add Agenda Item" },
	action: { placeholder: "Your action item goes here", addLabel: "Add Action Item" },
} as const satisfies Record<ItemType, { placeholder: string; addLabel: string }>;

/**
 * Inline form for creating/editing checklist items (agenda or action).
 * For action items, also renders Due Date and Assignee inline pickers.
 * Holds its own input state so the parent only needs to supply initial values
 * and receive the final payload via onSave.
 */
const ChecklistItemForm = ({
	id,
	itemType,
	initialName,
	initialDueDate,
	initialAssigneeId,
	isNewItem = false,
	workspaceParticipants,
	onSave,
}: ChecklistItemFormProps): React.ReactElement => {
	const isActionItem = itemType === "action";
	const testIdPrefix = isActionItem ? "action-item" : "agenda-item";
	const { placeholder, addLabel } = ITEM_TYPE_COPY[itemType];

	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [name, setName] = useState(initialName);
	const [dueDate, setDueDate] = useState<Date | null>(initialDueDate ?? null);
	const [assigneeId, setAssigneeId] = useState<string | null>(initialAssigneeId ?? null);
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const [isAssigneePickerOpen, setIsAssigneePickerOpen] = useState(false);
	const [assigneeSearch, setAssigneeSearch] = useState("");

	useEffect(() => {
		if (textareaRef.current) {
			textareaRef.current.select();
		}
	}, []);

	const buildSaveData = useCallback(
		(): ChecklistItemSaveData => ({
			name: name.trim(),
			...(isActionItem ? { dueDate, assigneeId } : {}),
		}),
		[name, dueDate, assigneeId, isActionItem]
	);

	const handleSave = useCallback((): void => {
		if (name.trim()) {
			onSave(true, buildSaveData());
		}
	}, [name, buildSaveData, onSave]);

	const handleCancel = useCallback((): void => {
		onSave(false);
	}, [onSave]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent): void => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSave();
			} else if (e.key === "Escape") {
				handleCancel();
			}
		},
		[handleSave, handleCancel]
	);

	const participants = workspaceParticipants ?? [];
	const assigneeName = participants.find((p) => p.userId === assigneeId)?.name;
	const filteredParticipants = assigneeSearch
		? participants.filter((p) => p.name?.toLowerCase().includes(assigneeSearch.toLowerCase()))
		: participants;

	const formatDate = (date: Date): string =>
		date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

	return (
		<div
			className="flex flex-col gap-2 rounded-sm border border-solid border-neutral-3 bg-neutral-0 px-2 pt-3 pb-2"
			data-testid={`${testIdPrefix}-${id}-form`}
		>
			<ReactTextareaAutosize
				ref={textareaRef}
				id={`${id}-input`}
				data-testid={`${testIdPrefix}-${id}-input`}
				className="w-full resize-none border-0 bg-transparent p-0 text-paragraph-3"
				value={name}
				onChange={(e) => setName(e.target.value)}
				placeholder={placeholder}
				minRows={config.layout.checklistInputMinRows}
				onKeyDown={handleKeyDown}
				autoFocus
			/>
			<div className="h-px w-full bg-neutral-2" />
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					{isActionItem &&
						(dueDate ? (
							<Tooltip
								content={`Due Date will be set for ${config.assignment.dueTimeLabel} in your current time zone`}
								placement="top"
								asChild
								dangerouslySetClassName="max-w-[180px]"
							>
								<div
									data-testid={`${testIdPrefix}-${id}-due-date-chip`}
									className="inline-flex shrink-0"
								>
									<Chip
										color="neutral"
										size={24}
										icon={faCalendarDays}
										label={formatDate(dueDate)}
										onDismiss={() => setDueDate(null)}
									/>
								</div>
							</Tooltip>
						) : (
							<Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
								<PopoverTrigger asChild>
									<ButtonStandard
										variant="outline"
										color="neutral"
										size="small"
										iconLeft={faCalendarDays}
										data-testid={`${testIdPrefix}-${id}-due-date-button`}
									>
										Due Date
									</ButtonStandard>
								</PopoverTrigger>
								<PopoverContent align="start" sideOffset={4} className="w-auto p-0">
									<Calendar
										mode="single"
										selected={dueDate ?? undefined}
										onSelect={(date: Date | undefined) => {
											if (date) {
												setDueDate(date);
												setIsDatePickerOpen(false);
											}
										}}
										showMonthAndYearDropdowns
										startDate={new Date()}
									/>
								</PopoverContent>
							</Popover>
						))}

					{isActionItem &&
						(assigneeId && assigneeName ? (
							<div data-testid={`${testIdPrefix}-${id}-assignee-chip`} className="inline-flex shrink-0">
								<Chip
									color="neutral"
									size={24}
									employeeId={assigneeId}
									employeeName={assigneeName}
									onDismiss={() => setAssigneeId(null)}
									dangerouslySetClassName="[&_p]:!text-caption"
								/>
							</div>
						) : (
							<Popover open={isAssigneePickerOpen} onOpenChange={setIsAssigneePickerOpen}>
								<PopoverTrigger asChild>
									<ButtonStandard
										variant="outline"
										color="neutral"
										size="small"
										iconLeft={faUser}
										data-testid={`${testIdPrefix}-${id}-assignee-button`}
									>
										Assignee
									</ButtonStandard>
								</PopoverTrigger>
								<PopoverContent
									align="start"
									sideOffset={4}
									className="w-auto min-w-[240px] overflow-clip rounded-sm border border-solid border-neutral-3 bg-neutral-0 p-0"
								>
									<div className="flex max-h-[288px] flex-col">
										<div className="flex-1 overflow-y-auto">
											{filteredParticipants.map((participant) => (
												<button
													key={participant.userId}
													type="button"
													className="flex h-8 w-full cursor-pointer items-center gap-2 border-none bg-neutral-0 px-2 py-1 text-left hover:bg-neutral-1"
													data-testid={`assignee-option-${participant.userId}`}
													onClick={() => {
														setAssigneeId(participant.userId);
														setIsAssigneePickerOpen(false);
														setAssigneeSearch("");
													}}
												>
													<AvatarProfile
														employeeId={participant.userId}
														employeeName={participant.name || ""}
														avatarSize={20}
														textSize="paragraph-3"
														noLink
													/>
												</button>
											))}
										</div>
										<div className="border-0 border-t border-solid border-neutral-2">
											<InputText
												value={assigneeSearch}
												onChange={setAssigneeSearch}
												leftIcon={faMagnifyingGlass}
												placeholder="Search..."
												id={`${id}-assignee-search`}
												data-testid={`${testIdPrefix}-${id}-assignee-search`}
												dangerouslySetClassName="!border-0 !rounded-none"
											/>
										</div>
									</div>
								</PopoverContent>
							</Popover>
						))}
				</div>
				<div className="flex items-center gap-2">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="small"
						onClick={handleCancel}
						data-testid={`${testIdPrefix}-${id}-cancel-button`}
					>
						Cancel
					</ButtonStandard>
					<ButtonStandard
						size="small"
						onClick={handleSave}
						disabled={!name.trim()}
						dangerouslySetClassName={
							!name.trim()
								? "disabled:cursor-not-allowed disabled:pointer-events-auto disabled:hover:!bg-primary-5"
								: ""
						}
						data-testid={`${testIdPrefix}-${id}-confirm-button`}
					>
						{isNewItem ? addLabel : "Save Changes"}
					</ButtonStandard>
				</div>
			</div>
		</div>
	);
};

ChecklistItemForm.displayName = "ChecklistItemForm";

export default ChecklistItemForm;
